import { create } from "zustand";
import { persist } from "zustand/middleware";
import CryptoJS from "crypto-js";

import type { Roles } from "@/entities/user";

interface Account {
  user: {
    id: string;
    username: string;
    role: Roles.TeamLead | Roles.MasterTeamLead;
  };
  tokens: {
    access: string;
    refresh: string;
  };
}

interface MasterTeamleadState {
  accounts: Account[];
  setAccounts: (newAccounts: Account[]) => void;
  addAccount: (account: Account) => void;
  removeAccount: (id: string) => void;
}

const SECRET_KEY = import.meta.env.VITE_ENCRYPT_TG_CODE || "lzkKZEOy7HAU2nEe";

export const useMasterTeamleadStore = create(
  persist<MasterTeamleadState>(
    (set) => ({
      accounts: [],
      setAccounts: (newAccounts: Account[]) =>
        set((state) => {
          if (JSON.stringify(state.accounts) !== JSON.stringify(newAccounts)) {
            return { accounts: newAccounts };
          }

          return { accounts: [...state.accounts] };
        }),
      addAccount: (account: Account) =>
        set((state) => {
          const exists = state.accounts.some(
            (acc) => acc.user.id === account.user.id,
          );
          if (exists) return state;

          return { accounts: [...state.accounts, account] };
        }),
      removeAccount: (id: string) =>
        set((state) => ({
          accounts: state.accounts.filter((acc) => acc.user.id !== id),
        })),
    }),
    {
      name: "master-teamlead-store",
      storage: {
        getItem: (name: string) => {
          const item = localStorage.getItem(name);
          if (!item) return null;

          try {
            const bytes = CryptoJS.AES.decrypt(item, SECRET_KEY);
            const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
            return decryptedData ? JSON.parse(decryptedData) : null;
          } catch (error) {
            console.error("Ошибка при расшифровке данных:", error);
            localStorage.removeItem(name);
            return null;
          }
        },
        setItem: (name: string, value: { state: MasterTeamleadState }) => {
          try {
            const encryptedData = CryptoJS.AES.encrypt(
              JSON.stringify(value),
              SECRET_KEY,
            ).toString();
            localStorage.setItem(name, encryptedData);
          } catch (error) {
            console.error("Ошибка при шифровании данных:", error);
          }
        },
        removeItem: (name: string) => {
          localStorage.removeItem(name);
        },
      },
    },
  ),
);
